/**
 * Display helpers for team-member names. Any UI surface that composes
 * "FirstName LastName" should route through these so nicknames are honored
 * consistently — e.g. "Art Jenuwine" instead of "Arthur Jenuwine" when
 * Arthur has entered "Art" in the Nickname field on his profile.
 *
 * Stored data is NOT changed by these helpers. SharePoint's FirstName,
 * LastName, and Title columns stay as the canonical legal form. This is a
 * render-only convenience so the roster feels natural in the UI while
 * keeping formal documents (proposals, invoices, PDFs) and system
 * references (email lookups, rename cascade) tied to legal names.
 */

/**
 * Preferred display first name. Falls back to FirstName when no nickname
 * is set. Returns an empty string when both are missing.
 */
export function displayFirstName(member) {
  if (!member) return '';
  const nick = (member.Nickname || '').trim();
  if (nick) return nick;
  return (member.FirstName || '').trim();
}

/**
 * Preferred "First Last" display name, honoring Nickname. Used as the
 * primary label in roster rows, detail pages, and org-structure cards.
 * Falls back to the SharePoint Title if both FirstName and LastName are
 * missing, so legacy rows still render something.
 */
export function displayFullName(member) {
  if (!member) return '';
  const first = displayFirstName(member);
  const last = (member.LastName || '').trim();
  const composed = [first, last].filter(Boolean).join(' ').trim();
  if (composed) return composed;
  return (member.Title || '').trim();
}

/**
 * Turn a stored legal-form name string (like "Arthur Jenuwine" sitting in
 * another member's RegionalManager or liaisonManagers field) into the
 * nickname-preferred display string. If the stored name doesn't match
 * anyone in the roster (stale reference, contractor not on roster, etc.),
 * the stored string is returned unchanged.
 */
export function displayStoredName(roster, storedName) {
  const needle = (storedName || '').trim();
  if (!needle || !Array.isArray(roster)) return needle;
  for (const m of roster) {
    const legal = [(m.FirstName || '').trim(), (m.LastName || '').trim()]
      .filter(Boolean)
      .join(' ');
    if (legal && legal === needle) return displayFullName(m);
    // Legacy rows may have only Title populated — match on that too.
    if ((m.Title || '').trim() === needle) return displayFullName(m);
  }
  return needle;
}
